import { useState, useCallback, useEffect, useRef } from "react";
import "./MathPractice.css";
import { awardToken } from "../../awardtoken";
import { db } from "../../firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../../components/useAuth";

/* ──────── Constants & Helpers ──────── */
const SOUND_SOURCES = ["/sounds/correct.mp3", "/sounds/correct.ogg", "/sounds/correct.wav"];
const SHEET_SIZES = [10, 25, 50];
const PI = 3.14;

const MODES = {
  AREA: "area",
  PERIMETER: "perimeter",
  VOLUME: "volume",
  SURFACE: "surface",
};

const DIFFICULTY = {
  EASY:   { min: 2,  max: 10, decimals: false, label: "Easy" },
  MEDIUM: { min: 3,  max: 20, decimals: false, label: "Medium" },
  HARD:   { min: 1.5, max: 30, decimals: true, label: "Hard" },
};

const SHAPES_2D = ["rectangle", "square", "triangle", "circle"];
const SHAPES_3D = ["rectPrism", "cube", "cylinder"];

function rand(min, max, allowDecimal = false) {
  if (allowDecimal) {
    const n = Math.random() * (max - min) + min;
    return parseFloat(n.toFixed(1));
  }
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/* ──────── Problem Generator ──────── */
function genProblem({ mode = MODES.AREA, diff = DIFFICULTY.MEDIUM } = {}) {
  const allowDecimal = diff.decimals;
  const { min, max } = diff;
  const u = (v) => `${v} units`;

  // Restrict shape pool by mode
  let shapePool;
  if (mode === MODES.VOLUME || mode === MODES.SURFACE) {
    shapePool = SHAPES_3D;
  } else {
    shapePool = SHAPES_2D;
  }
  const shape = shapePool[rand(0, shapePool.length - 1)];

  switch (shape) {
    // 2D
    case "rectangle": {
      const w = rand(min, max, allowDecimal);
      const h = rand(min, max, allowDecimal);
      if (mode === MODES.PERIMETER) {
        return {
          shape, dims: { w, h },
          question: `Find the <b>perimeter</b> of a rectangle whose width is ${u(w)} and height is ${u(h)}.`,
          answer: parseFloat((2 * (w + h)).toFixed(2)),
        };
      }
      if (mode === MODES.AREA) {
        return {
          shape, dims: { w, h },
          question: `Find the <b>area</b> of a rectangle whose width is ${u(w)} and height is ${u(h)}.`,
          answer: parseFloat((w * h).toFixed(2)),
        };
      }
      // Should not get here in volume/surface modes!
      return genProblem({ mode, diff }); // retry
    }
    case "square": {
      const s = rand(min, max, allowDecimal);
      if (mode === MODES.PERIMETER) {
        return {
          shape, dims: { s },
          question: `Find the <b>perimeter</b> of a square whose side length is ${u(s)}.`,
          answer: parseFloat((4 * s).toFixed(2)),
        };
      }
      if (mode === MODES.AREA) {
        return {
          shape, dims: { s },
          question: `Find the <b>area</b> of a square whose side length is ${u(s)}.`,
          answer: parseFloat((s * s).toFixed(2)),
        };
      }
      return genProblem({ mode, diff });
    }
    case "triangle": {
      const b = rand(min, max, allowDecimal), h = rand(min, max, allowDecimal);
      const hyp = Math.sqrt(b * b + h * h);
      if (mode === MODES.PERIMETER) {
        return {
          shape, dims: { b, h },
          question: `Find the <b>perimeter</b> of a right triangle with base ${u(b)} and height ${u(h)}.`,
          answer: parseFloat((b + h + hyp).toFixed(2)),
        };
      }
      if (mode === MODES.AREA) {
        return {
          shape, dims: { b, h },
          question: `Find the <b>area</b> of a right triangle with base ${u(b)} and height ${u(h)}.`,
          answer: parseFloat(((b * h) / 2).toFixed(2)),
        };
      }
      return genProblem({ mode, diff });
    }
    case "circle": {
      const r = rand(min, max, allowDecimal);
      if (mode === MODES.PERIMETER) {
        return {
          shape, dims: { r },
          question: `Find the <b>circumference</b> of a circle with radius ${u(r)} (use π ≈ 3.14).`,
          answer: parseFloat((2 * PI * r).toFixed(2)),
        };
      }
      if (mode === MODES.AREA) {
        return {
          shape, dims: { r },
          question: `Find the <b>area</b> of a circle with radius ${u(r)} (use π ≈ 3.14).`,
          answer: parseFloat((PI * r * r).toFixed(2)),
        };
      }
      return genProblem({ mode, diff });
    }
    // 3D shapes:
    case "rectPrism": {
      const l = rand(min, max, allowDecimal),
            w = rand(min, max, allowDecimal),
            h = rand(min, max, allowDecimal);
      if (mode === MODES.SURFACE) {
        const sa = 2 * (l * w + l * h + w * h);
        return {
          shape: "rectPrism",
          dims: { l, w, h },
          question: `Find the <b>surface area</b> of a rectangular prism with length ${u(l)}, width ${u(w)}, and height ${u(h)}.`,
          answer: parseFloat(sa.toFixed(2)),
        };
      }
      if (mode === MODES.VOLUME) {
        return {
          shape: "rectPrism",
          dims: { l, w, h },
          question: `Find the <b>volume</b> of a rectangular prism with length ${u(l)}, width ${u(w)}, and height ${u(h)}.`,
          answer: parseFloat((l * w * h).toFixed(2)),
        };
      }
      return genProblem({ mode, diff }); // retry
    }
    case "cube": {
      const s = rand(min, max, allowDecimal);
      if (mode === MODES.SURFACE) {
        const sa = 6 * s * s;
        return {
          shape: "cube",
          dims: { s },
          question: `Find the <b>surface area</b> of a cube whose edge length is ${u(s)}.`,
          answer: parseFloat(sa.toFixed(2)),
        };
      }
      if (mode === MODES.VOLUME) {
        return {
          shape: "cube",
          dims: { s },
          question: `Find the <b>volume</b> of a cube whose edge length is ${u(s)}.`,
          answer: parseFloat((s ** 3).toFixed(2)),
        };
      }
      return genProblem({ mode, diff });
    }
    case "cylinder": {
      const r = rand(min, max, allowDecimal),
            h = rand(min, max, allowDecimal);
      if (mode === MODES.SURFACE) {
        const sa = 2 * PI * r * r + 2 * PI * r * h;
        return {
          shape: "cylinder",
          dims: { r, h },
          question: `Find the <b>surface area</b> of a cylinder with radius ${u(r)} and height ${u(h)} (use π ≈ 3.14).`,
          answer: parseFloat(sa.toFixed(2)),
        };
      }
      if (mode === MODES.VOLUME) {
        return {
          shape: "cylinder",
          dims: { r, h },
          question: `Find the <b>volume</b> of a cylinder with radius ${u(r)} and height ${u(h)} (use π ≈ 3.14).`,
          answer: parseFloat((PI * r * r * h).toFixed(2)),
        };
      }
      return genProblem({ mode, diff });
    }
    default:
      return null;
  }
}


const genSet = (qty, options) => Array.from({ length: qty }, () => genProblem(options));

/* ──────── SVG Illustrations ──────── */
const SvgText = ({ x, y, children, ...rest }) => (
  <text x={x} y={y} fontSize="12" textAnchor="middle" dominantBaseline="middle" {...rest}>
    {children}
  </text>
);

function Illustration({ problem, mode }) {
  if (!problem) return null;
  const { shape, dims } = problem;

  switch (shape) {
    case "rectangle": {
      const { w, h } = dims;
      return (
        <svg viewBox="0 0 100 100" width="100" height="100">
          {/* Rectangle shape */}
          <rect x="15" y="30" width="70" height="60" fill="#eafcff" stroke="#2662a8" strokeWidth="2" />
          {/* width label, with white bg */}
          <rect x="30" y="14" width="40" height="18" fill="#fff" stroke="none" rx="4" />
          <SvgText x="50" y="26" fontWeight="bold">{`w=${w}`}</SvgText>
          {/* height label, with white bg */}
          <rect x="3" y="54" width="22" height="18" fill="#fff" stroke="none" rx="4" />
          <SvgText x="14" y="66" transform="rotate(-90,14,66)" fontWeight="bold">{`h=${h}`}</SvgText>
        </svg>
      );
    }
    case "square": {
      const { s } = dims;
      return (
        <svg viewBox="0 0 100 100" width="100" height="100">
          <rect x="15" y="30" width="70" height="70" fill="#eafcff" stroke="#2662a8" strokeWidth="2" />
          <rect x="30" y="14" width="40" height="18" fill="#fff" stroke="none" rx="4" />
          <SvgText x="50" y="26" fontWeight="bold">{`s=${s}`}</SvgText>
        </svg>
      );
    }
    case "triangle": {
      const { b, h } = dims;
      return (
        <svg viewBox="0 0 100 100" width="100" height="100">
          <polygon points="10,80 90,80 10,10" fill="#eafcff" stroke="#2662a8" strokeWidth="2" />
          <rect x="35" y="86" width="30" height="14" fill="#fff" stroke="none" rx="4" />
          <SvgText x="50" y="96" fontWeight="bold">{`b=${b}`}</SvgText>
          <rect x="3" y="34" width="22" height="18" fill="#fff" stroke="none" rx="4" />
          <SvgText x="14" y="45" transform="rotate(-90,14,45)" fontWeight="bold">{`h=${h}`}</SvgText>
        </svg>
      );
    }
    case "circle": {
      const { r } = dims;
      return (
        <svg viewBox="0 0 100 100" width="100" height="100">
          <circle cx="50" cy="50" r="30" fill="#eafcff" stroke="#2662a8" strokeWidth="2" />
          <rect x="34" y="36" width="32" height="18" fill="#fff" stroke="none" rx="4" />
          <SvgText x="50" y="48" fontWeight="bold">{`r=${r}`}</SvgText>
        </svg>
      );
    }
    case "rectPrism": {
      const { l, w, h } = dims;
      return (
        <svg viewBox="0 0 100 100" width="100" height="100">
          {/* Isometric box faces */}
          <polygon points="20,20 70,10 90,35 40,45" fill="#eafcff" stroke="#2662a8" strokeWidth="2"/>
          <polygon points="20,20 20,60 40,85 40,45" fill="#d6f0ff" stroke="#2662a8" strokeWidth="2"/>
          <polygon points="90,35 90,75 40,85 40,45" fill="#b7e4ff" stroke="#2662a8" strokeWidth="2"/>
          {/* Labels */}
          <rect x="47" y="0" width="26" height="14" fill="#fff" stroke="none" rx="4" />
          <SvgText x="60" y="10" fontWeight="bold">{`l=${l}`}</SvgText>
          <rect x="0" y="38" width="22" height="18" fill="#fff" stroke="none" rx="4" />
          <SvgText x="12" y="48" transform="rotate(-90,12,48)" fontWeight="bold">{`h=${h}`}</SvgText>
          <rect x="54" y="84" width="32" height="14" fill="#fff" stroke="none" rx="4" />
          <SvgText x="70" y="95" fontWeight="bold">{`w=${w}`}</SvgText>
        </svg>
      );
    }
    case "cube": {
      const { s } = dims;
      return (
        <svg viewBox="0 0 100 100" width="100" height="100">
          <polygon points="20,20 70,10 90,35 40,45" fill="#eafcff" stroke="#2662a8" strokeWidth="2"/>
          <polygon points="20,20 20,60 40,85 40,45" fill="#d6f0ff" stroke="#2662a8" strokeWidth="2"/>
          <polygon points="90,35 90,75 40,85 40,45" fill="#b7e4ff" stroke="#2662a8" strokeWidth="2"/>
          <rect x="37" y="0" width="26" height="14" fill="#fff" stroke="none" rx="4" />
          <SvgText x="50" y="10" fontWeight="bold">{`s=${s}`}</SvgText>
        </svg>
      );
    }
    case "cylinder": {
      const { r, h } = dims;
      return (
        <svg viewBox="0 0 100 100" width="100" height="100">
          <ellipse cx="50" cy="20" rx="25" ry="10" fill="#eafcff" stroke="#2662a8" strokeWidth="2"/>
          <rect x="25" y="20" width="50" height="45" fill="#d6f0ff" stroke="#2662a8" strokeWidth="2"/>
          <ellipse cx="50" cy="65" rx="25" ry="10" fill="#b7e4ff" stroke="#2662a8" strokeWidth="2"/>
          <rect x="36" y="10" width="28" height="14" fill="#fff" stroke="none" rx="4" />
          <SvgText x="50" y="20" fontWeight="bold">{`r=${r}`}</SvgText>
          <rect x="0" y="42" width="22" height="18" fill="#fff" stroke="none" rx="4" />
          <SvgText x="11" y="52" transform="rotate(-90,11,52)" fontWeight="bold">{`h=${h}`}</SvgText>
        </svg>
      );
    }
    default: return null;
  }
}

/* ──────── Worksheet column ──────── */
const WSCol = ({ problem, mode }) => (
  <div className="math-col">
    <Illustration problem={problem} mode={mode}/>
    <p className="geometry-question" dangerouslySetInnerHTML={{ __html: problem.question }} />
    <div className="math-underline" />
  </div>
);

const TOOL_TYPE = "MathGeometryPractice";

async function logGeometryPractice({ user, mode, difficulty, numQuestions, correct, attempts, startTime, endTime }) {
  if (!user?.uid) {
    console.warn("No user UID! User:", user);
    return;
  }
  if (attempts === 0) {
    console.warn("No attempts, nothing to log.");
    return;
  }
  try {
    await setDoc(
      doc(db, "users", user.uid, "practiceRecords", TOOL_TYPE),
      {
        toolType: TOOL_TYPE,
        topic: `${mode} (${difficulty.label})`,
        numQuestions,
        correct,
        attempts,
        timestamp: serverTimestamp(),
        startedAt: startTime,
        finishedAt: endTime,
        durationSec: Math.round((endTime - startTime) / 1000),
      },
      { merge: true }
    );
    console.log("Geometry session log successfully written to Firestore.");
  } catch (e) {
    console.error("Error writing geometry session log:", e);
  }
}


/* ──────── Main component ──────── */
export default function MathGeometryPractice({ onAwardTokens } = {}) {
  const [ans, setAns] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [correct, setCorrect]  = useState(0);
  const [sheet, setSheet] = useState(null);
  const [mode, setMode] = useState(MODES.AREA);
  const [difficulty, setDifficulty] = useState(DIFFICULTY.MEDIUM);
  const [problem, setProblem] = useState(() => genProblem({ mode, diff: difficulty }));
  const [feedback, setFeedback] = useState(null);
  const [waitingForNext, setWaitingForNext] = useState(false);
  const { user } = useAuth();
  const [sessionStart, setSessionStart] = useState(Date.now());
  const sessionStats = useRef({ attempts: 0, correct: 0 });
  const didMountRef = useRef(false);

  const audioRef = useRef(null);
  const tokensAwarded = useRef(0);   // how many we’ve given in this session

  useEffect(() => {
  if (!didMountRef.current) {
    didMountRef.current = true;
    setProblem(genProblem({ mode, diff: difficulty }));
    return;
  }
  const endTime = Date.now();
  if (sessionStats.current.attempts > 0) {
    logGeometryPractice({
      user,
      mode,
      difficulty,
      numQuestions: sessionStats.current.attempts,
      correct: sessionStats.current.correct,
      attempts: sessionStats.current.attempts,
      startTime: sessionStart,
      endTime,
    });
  }
  setSessionStart(Date.now());
  setAttempts(0);
  setCorrect(0);
  sessionStats.current = { attempts: 0, correct: 0 };
  setProblem(genProblem({ mode, diff: difficulty }));
  setAns("");
  // eslint-disable-next-line
}, [mode, difficulty]);

useEffect(() => {
  return () => {
    const endTime = Date.now();
    if (sessionStats.current.attempts > 0) {
      logGeometryPractice({
        user,
        mode,
        difficulty,
        numQuestions: sessionStats.current.attempts,
        correct: sessionStats.current.correct,
        attempts: sessionStats.current.attempts,
        startTime: sessionStart,
        endTime,
      });
    }
  };
  // eslint-disable-next-line
}, []);


  useEffect(() => {
  // Each time correct reaches a new multiple of 10, give one token
  if (correct >= (tokensAwarded.current + 1) * 10) {
    awardToken();
    tokensAwarded.current += 1;

    // tell parent (e.g., dashboard) if they asked for notifications
    if (onAwardTokens) onAwardTokens(1);

    // optional: refresh local user data if you display coins/avatars
    // const updated = JSON.parse(localStorage.getItem("loggedInUser") || "null");
    // if (updated) setUser(updated);
  }
}, [correct, onAwardTokens]);

  // Sync problem when mode/difficulty changes
  useEffect(() => {
    setProblem(genProblem({ mode, diff: difficulty }));
  }, [mode, difficulty]);

  /* Sound initialisation */
  useEffect(() => {
    const audio = new Audio();
    for (const src of SOUND_SOURCES) {
      const type = src.endsWith(".mp3") ? "audio/mpeg"
        : src.endsWith(".ogg") ? "audio/ogg"
        : "audio/wav";
      if (audio.canPlayType(type)) { audio.src = src; break; }
    }
    audioRef.current = audio;
  }, []);

  /* Helpers */
  const resetStats = useCallback(() => { setAttempts(0); setCorrect(0); }, []);
  const nextProblem = () => setProblem(genProblem({ mode, diff: difficulty }));

const submit = (e) => {
  e.preventDefault();
  if (!ans.trim() || waitingForNext) return;
  const ok = Math.abs(parseFloat(ans) - problem.answer) < 0.01;
  const nextAttempts = attempts + 1;
  const nextCorrect = ok ? correct + 1 : correct;
  setAttempts(nextAttempts);
  setCorrect(nextCorrect);
  sessionStats.current = { attempts: nextAttempts, correct: nextCorrect };

  // Log every answer—consistent with other modules!
  const endTime = Date.now();
  logGeometryPractice({
    user,
    mode,
    difficulty,
    numQuestions: nextAttempts,
    correct: nextCorrect,
    attempts: nextAttempts,
    startTime: sessionStart,
    endTime,
  });

  if (ok) {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
    setFeedback("✅ Correct!");
    setAns("");
    setTimeout(() => {
      setFeedback(null);
      nextProblem();
    }, 700);
    setWaitingForNext(false);
  } else {
    setFeedback(`❌ The correct answer is: ${problem.answer}`);
    setWaitingForNext(true);
  }
};


  // Print/worksheet uses current mode/difficulty
  const startSheet = (n) => {
    resetStats();
    setSheet(genSet(n, { mode, diff: difficulty }));
    setTimeout(() => window.print(), 50);
  };

  /* Render: Worksheet */
  if (sheet) {
    return (
      <div className="worksheet">
        <h2 className="no-print" style={{ display: "flex", justifyContent: "space-between" }}>
          Geometry Worksheet
          <span>
            <button onClick={() => setSheet(null)} style={{ marginLeft: "0.5rem" }}>Back to Practice</button>
          </span>
        </h2>
        <div className="sheet-grid">
          {sheet.map((p, i) => <WSCol key={i} problem={p} />)}
        </div>
      </div>
    );
  }

  /* Render: Interactive Practice */
  return (
    <div className="practice-container">
      <h2 style={{ display: "flex", justifyContent: "space-between" }}>
        Geometry Practice
      </h2>
      {/* Controls */}
      <div className="control-row">
        <label>
          Mode:&nbsp;
          <select value={mode} onChange={e => setMode(e.target.value)}>
              <option value={MODES.AREA}>Area</option>
              <option value={MODES.PERIMETER}>Perimeter / Circumference</option>
              <option value={MODES.VOLUME}>Volume (3D)</option>
              <option value={MODES.SURFACE}>Surface Area (3D)</option> {/* NEW */}
          </select>
        </label>
        <br></br>
        <label style={{ marginLeft: "1rem" }}>
          Difficulty:&nbsp;
          <select value={difficulty.label}
            onChange={e => setDifficulty(DIFFICULTY[e.target.value])}
          >
            {Object.entries(DIFFICULTY).map(([key, obj]) =>
              <option key={key} value={key}>{obj.label}</option>
            )}
          </select>
        </label>
      </div>
      {/* Problem */}
      <div className="current-question" dangerouslySetInnerHTML={{ __html: problem.question }} />
      <Illustration problem={problem} mode={mode}/>
      <form onSubmit={submit} className="answer-form">
        <input
          type="number"
          step="0.01"
          value={ans}
          onChange={e => setAns(e.target.value)}
          autoFocus
        />
        <button type="submit">Submit</button>
      </form>
      {feedback && (
  <div className="math-practice-feedback" style={{ marginBottom: 7 }}>
    {feedback}
  </div>
)}
{waitingForNext && (
  <button
    className="math-pill-btn"
    style={{ marginTop: "0.7em" }}
    onClick={() => {
      setFeedback(null);
      setAns("");
      setWaitingForNext(false);
      nextProblem();
    }}
  >
    Next
  </button>
)}
      <p>Score: {correct} / {attempts}</p>
      <button
  className="math-pill-btn"
  style={{ margin: "1em 0" }}
  onClick={() => {
    const endTime = Date.now();
    if (sessionStats.current.attempts > 0) {
      logGeometryPractice({
        user,
        mode,
        difficulty,
        numQuestions: sessionStats.current.attempts,
        correct: sessionStats.current.correct,
        attempts: sessionStats.current.attempts,
        startTime: sessionStart,
        endTime,
      });
      setSessionStart(Date.now());
      setAttempts(0);
      setCorrect(0);
      sessionStats.current = { attempts: 0, correct: 0 };
    }
  }}
>
  Finish Session
</button>


      <div className="worksheet-buttons">
        <strong>Printable worksheets:</strong>
        {SHEET_SIZES.map((n) => (
          <button key={n} onClick={() => startSheet(n)}>{n} Questions</button>
        ))}
      </div>
    </div>
  );
}
